// Vue-based enhancements and ToC builder replacing jQuery usages
// Loads after DOMContentLoaded; requires Vue 3 (global build)

(() => {
  const onReady = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  function enhanceExternalLinks() {
    try {
      const links = document.querySelectorAll('.entry-content a[href*="http"]');
      links.forEach((a) => {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', a.getAttribute('rel') ? a.getAttribute('rel') + ' noopener noreferrer' : 'noopener noreferrer');
      });
    } catch (e) {
      // no-op
    }
  }

  function enhanceYouTubeEmbeds() {
    try {
      const wrappers = document.querySelectorAll('.youtube-responsive-wrapper');
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      wrappers.forEach((wrap) => {
        if (isTouch) wrap.classList.add('touch-device');
        const iframe = wrap.querySelector('iframe');
        if (iframe) {
          iframe.addEventListener('load', () => {
            const container = wrap.closest('.youtube-video-container') || wrap.parentElement;
            if (container) container.classList.add('loaded');
          }, { once: true });
        }
      });
    } catch (e) {
      // no-op
    }
  }

  function initFloatShare() {
    try {
      const minWidth = 768;
      if (window.innerWidth < minWidth) return;

      const getCSSVarInt = (el, name, fallback = 0) => {
        const v = window.getComputedStyle(el).getPropertyValue(name);
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? fallback : n;
      };

      const docEl = document.documentElement;
      const mainWidth = getCSSVarInt(docEl, '--main-width', 980);

      // Determine viewport fit classes
      document.querySelectorAll('.has-share-float').forEach((host) => {
        if ((mainWidth + 110) > document.documentElement.clientWidth) {
          host.classList.add('has-share-float-in');
        } else {
          host.classList.remove('has-share-float-in');
          const side = (window.innerWidth - mainWidth) / 2;
          const floatEl = host.querySelector('.post-share-float');
          if (floatEl) {
            if (side < 110) floatEl.classList.add('post-share-float-vp');
            else floatEl.classList.remove('post-share-float-vp');
          }
        }
      });

      // Compute base top offset
      let topOffset = 25;
      if (document.querySelector('.admin-bar')) topOffset += 32;

      const setInnerTop = (el, px) => {
        const inner = el.querySelector('.inner');
        if (inner) inner.style.top = `${px}px`;
      };

      // Intersection check to toggle hidden state (rough port of original logic)
      const blockers = Array.from(document.querySelectorAll([
        '.the-post-modern .alignfull',
        '.the-post-modern .alignwide',
        '.the-post-modern .gallery:not(.aligncenter)',
        '.the-post-modern .wp-block-gallery:not(.aligncenter)',
        '.the-post-modern figure.alignnone',
        '.the-post-modern img.alignnone'
      ].join(',')));

      const onScrollCheck = (floatEl) => {
        if (!floatEl || !floatEl.isConnected) return;
        const inner = floatEl.querySelector('.inner');
        if (!inner) return;

        const iw = window.innerWidth;
        if (iw < minWidth) return;

        const wrapTop = (() => {
          const post = floatEl.closest('.the-post');
          const header = post ? post.querySelector('.the-post-header') : null;
          return header ? (header.getBoundingClientRect().top + window.scrollY) : 0;
        })();

        let hide = false;
        if (window.scrollY < wrapTop) hide = true;
        else {
          const innerRect = inner.getBoundingClientRect();
          for (let i = 0; i < blockers.length; i++) {
            const br = blockers[i].getBoundingClientRect();
            const noOverlap = (innerRect.top > br.bottom || innerRect.right < br.left || innerRect.bottom < br.top || innerRect.left > br.right);
            if (!noOverlap) { hide = true; break; }
          }
        }
        if (hide) floatEl.classList.add('is-hidden');
        else floatEl.classList.remove('is-hidden');
      };

      const floats = Array.from(document.querySelectorAll('.post-share-float'));
      floats.forEach((el) => {
        if (el.dataset.init) return;
        el.dataset.init = '1';
        setInnerTop(el, topOffset);
        // Listen to custom sticky events (native + jQuery bridge)
        const stickyHeader = () => document.querySelector('.smart-head-sticky');
        const getStickyH = () => {
          const sh = stickyHeader();
          return sh ? getCSSVarInt(sh, '--head-h', 0) : 0;
        };
        document.addEventListener('sticky-bar-show', () => setInnerTop(el, topOffset + Math.max(0, getStickyH() - 10)));
        document.addEventListener('sticky-bar-hide', () => setInnerTop(el, topOffset));
        if (window.jQuery) {
          const $doc = window.jQuery(document);
          $doc.on('sticky-bar-show', () => setInnerTop(el, topOffset + Math.max(0, getStickyH() - 10)));
          $doc.on('sticky-bar-hide', () => setInnerTop(el, topOffset));
        }
        // Initial scroll state + listener
        onScrollCheck(el);
        window.addEventListener('scroll', () => {
          window.requestAnimationFrame(() => onScrollCheck(el));
        });
        window.addEventListener('resize', () => {
          window.requestAnimationFrame(() => onScrollCheck(el));
        });
      });
    } catch (e) {
      // no-op
    }
  }

  function mountToC() {
    const target = document.querySelector('#toc');
    if (!target || !window.Vue || !window.Vue.createApp) return;

    const { createApp, ref, onMounted } = window.Vue;

    createApp({
      setup() {
        const items = ref([]);
        const activeId = ref('');

        const buildId = (el, index) => {
          const base = (el.id && el.id.trim()) || (el.textContent || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
          const id = base || `toc-${index}`;
          if (!el.id) el.id = id;
          return el.id;
        };

        const collect = () => {
          const roots = Array.from(document.querySelectorAll('.article-wrap'));
          const scope = roots.length ? roots : [document];
          const hs = [];
          scope.forEach((root) => {
            hs.push(...root.querySelectorAll('h1, h2, h3, h4, h5, h6'));
          });
          const out = [];
          hs.forEach((el, idx) => {
            const level = Number(el.tagName.substring(1));
            const id = buildId(el, idx);
            const text = (el.textContent || '').trim();
            out.push({ id, level, text });
          });
          items.value = out;
        };

        const scrollTo = (id) => {
          const el = document.getElementById(id);
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const offset = 80; // similar to legacy plugin offset
          const top = rect.top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        };

        onMounted(() => {
          collect();
          // Scroll spy
          const obs = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                activeId.value = entry.target.id;
              }
            });
          }, { rootMargin: '0px 0px -80% 0px', threshold: [0, 1.0] });
          items.value.forEach((it) => {
            const el = document.getElementById(it.id);
            if (el) obs.observe(el);
          });
        });

        return { items, activeId, scrollTo };
      },
      template: `
        <ul>
          <li v-for="it in items" :key="it.id" class="index-item" :class="[{ current: activeId === it.id }, 'toc-h' + it.level]">
            <a class="index-link" href="#" @click.prevent="scrollTo(it.id)">{{ it.text }}</a>
          </li>
        </ul>
      `
    }).mount(target);
  }

  function mountEnhancerApp() {
    if (!window.Vue || !window.Vue.createApp) return;
    const { createApp, onMounted } = window.Vue;
    const host = document.createElement('div');
    host.id = 'page-vue-root';
    host.style.display = 'none';
    document.body.appendChild(host);
    createApp({
      setup() {
        onMounted(() => {
          enhanceExternalLinks();
          enhanceYouTubeEmbeds();
          initFloatShare();
        });
      },
      template: '<div></div>'
    }).mount(host);
  }

  onReady(() => {
    // Ensure Vue is available before mounting apps
    mountEnhancerApp();
    mountToC();
  });
})();
